// Playful editorial accent - three cartoon strips from the queencleopatra.co.uk archive.
// Black-and-white humour panels that show another side of the practice: lighter,
// theatrical, knowingly tongue-in-cheek. Click to enlarge in a lightbox.
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { X } from "lucide-react";

const CARTOONS = [
  {
    id: "cartoon-1",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/n3gj63gu_WhatsApp%20Image%202026-06-15%20at%2023.10.29.jpeg",
    alt: "Cartoon - hypnotist swinging a pocket watch, subject lost in a happy fantasy",
    caption: "The Daydream",
  },
  {
    id: "cartoon-2",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/dedg5tlg_WhatsApp%20Image%202026-06-15%20at%2023.10.29%20%281%29.jpeg",
    alt: "Cartoon - a sly Queen Cleopatra mid-induction",
    caption: "The Induction",
  },
  {
    id: "cartoon-3",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/quisz5he_WhatsApp%20Image%202026-06-15%20at%2023.10.29%20%282%29.jpeg",
    alt: "Cartoon - a comic moment of suggestion and surrender",
    caption: "The Suggestion",
  },
];

export default function CartoonStrip() {
  const [active, setActive] = useState(null);

  return (
    <section
      data-testid="cartoon-strip"
      className="relative py-24 md:py-32 border-y border-[#c9a04b]/15 bg-charcoalMuted grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">A Lighter Side</div>
            <h2 className="font-serif text-4xl md:text-5xl text-cream leading-tight">
              From the <em className="text-gold">comic archive</em>.
            </h2>
          </div>
          <div className="md:col-span-5 text-cream/65 leading-relaxed text-sm md:text-base">
            Long-time readers will remember these. A small collection of strips drawn
            for the practice - playful, knowing, and entirely on-brand.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#c9a04b]/15">
          {CARTOONS.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c)}
              data-testid={`cartoon-${c.id}`}
              aria-label={`Enlarge: ${c.caption}`}
              className="group relative bg-cream p-4 md:p-6 flex flex-col"
            >
              <div className="relative overflow-hidden bg-cream">
                <img
                  src={c.src}
                  alt={c.alt}
                  loading="lazy"
                  className="w-full h-auto transition-transform duration-[1200ms] group-hover:scale-[1.02]"
                />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/70">{c.caption}</span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/40 group-hover:text-[#7a3f5f] transition-colors">Enlarge →</span>
              </div>
            </button>
          ))}
        </div>

        <p className="mt-8 text-xs text-cream/40 leading-relaxed tracking-wider max-w-2xl">
          Original illustrations for Queen Cleopatra - reproduced with permission. Adults 18+.
        </p>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-4xl w-[95vw] p-0 border-[#c9a04b]/40 bg-cream rounded-none [&>button]:hidden"
          data-testid="cartoon-lightbox"
        >
          {active && (
            <>
              <DialogTitle className="sr-only">{active.caption}</DialogTitle>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="cartoon-lightbox-close"
                className="absolute top-3 right-3 z-20 w-10 h-10 inline-flex items-center justify-center border border-charcoal/30 bg-cream/90 backdrop-blur text-charcoal hover:text-[#7a3f5f] hover:border-[#7a3f5f] transition-colors"
              >
                <X size={16} />
              </button>
              <img
                src={active.src}
                alt={active.alt}
                className="w-full max-h-[85vh] object-contain bg-cream"
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
